import './assets/main.css'
import '@fontsource-variable/inter'
import { initTheme } from '@/composables/useTheme'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import App from './App.vue'
import router from './router'
import KeycloakPlugin, { keycloak } from './plugins/keycloak'
import { createI18n } from 'vue-i18n'
import { numberFormats, datetimeFormats, loadLocaleMessages } from '@/translations'
import createApiPlugin from './plugins/api'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import ky from 'ky'
import * as Sentry from '@sentry/vue'
import { useAppStore } from '@/stores/app'
import { checkUser, renderBootstrapError } from '@/bootstrap'
import { resolveRefreshInterval, startTokenRefresh } from '@/plugins/tokenRefresh'

async function createAppI18n() {
  const initialLocale = localStorage.getItem('locale') || 'it'

  const i18n = createI18n({
    locale: initialLocale,
    fallbackLocale: 'it',
    messages: {}, // Empty initially - will be loaded lazily
    numberFormats,
    datetimeFormats,
    legacy: false
  })

  // Load locale messages asynchronously
  await loadLocaleMessages(i18n, initialLocale)
  return i18n
}

/** Errore di avvio: sostituisce il loader con messaggio e "Riprova", invece dello spinner infinito. */
type AppI18n = Awaited<ReturnType<typeof createAppI18n>>

async function showBootstrapError(i18n: AppI18n | null) {
  let texts = { title: 'Servizio non disponibile', message: '', retry: 'Riprova' }
  try {
    const { global } = i18n ?? (await createAppI18n())
    texts = {
      title: global.t('errors.bootstrap.title'),
      message: global.t('errors.bootstrap.message'),
      retry: global.t('errors.bootstrap.retry')
    }
  } catch (err) {
    console.error('Bootstrap error texts unavailable:', err)
  }
  renderBootstrapError(document, texts, () => window.location.reload())
}

async function initKeycloak() {
  let i18n: AppI18n | null = null
  let stopTokenRefresh: () => void = () => {}
  try {
    const authenticated = await keycloak.init({ onLoad: 'login-required' })
    if (authenticated) {
      initTheme()

      i18n = await createAppI18n()

      let toastInstance: any = null

      const appI18n = i18n
      stopTokenRefresh = startTokenRefresh(keycloak, {
        intervalMs: resolveRefreshInterval(import.meta.env.VITE_REFRESH_TOKEN_INTERVAL),
        onFailure: (attempt, max) => {
          Sentry.captureMessage('Token refresh failed', {
            level: 'warning',
            tags: { attempt: attempt.toString() }
          })
          console.warn(`Token refresh failed (attempt ${attempt}/${max})`)
          toastInstance?.add({
            severity: 'warn',
            summary: appI18n.global.t('errors.tokenRefresh'),
            detail: appI18n.global.t('errors.tokenRefreshDetail', { attempt, max }),
            life: 5000
          })
        },
        onExhausted: () => {
          console.error('Token refresh failed after max retries, redirecting to login')
          Sentry.captureMessage('Token refresh exhausted, redirecting to login', { level: 'error' })
          keycloak.logout({ redirectUri: window.location.origin })
        }
      })

      if (!(await checkUser(createCheckUserClient(), stopTokenRefresh))) {
        console.error('Bootstrap aborted: check-user failed')
        await showBootstrapError(i18n)
        return
      }

      const app = createApp(App)

      app.config.errorHandler = (err, instance, info) => {
        const componentName = instance?.$options?.name || instance?.$.type?.name || 'unknown'
        console.error(`[Vue Error] in ${componentName} (${info}):`, err)
        Sentry.captureException(err, {
          contexts: {
            vue: { component: componentName, info }
          }
        })
      }

      Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        release: import.meta.env.VITE_APP_VERSION
      })
      app.use(createPinia())

      // Set i18n instance in app store for locale switching
      const appStore = useAppStore()
      appStore.setI18n(i18n)

      app.use(router)
      app.use(i18n)
      app.use(KeycloakPlugin)
      app.use(createApiPlugin(keycloak))
      app.use(ToastService)
      // Unstyled: lo stile vive nei wrapper di src/components/ui. Il tooltip è una direttiva,
      // senza wrapper, quindi il suo pass-through è globale.
      app.use(PrimeVue, {
        unstyled: true,
        pt: {
          directives: {
            tooltip: {
              root: 'absolute z-50 max-w-xs p-1',
              text: 'rounded-md bg-surface-900 px-2 py-1 text-xs text-surface-0 shadow-md dark:bg-surface-100 dark:text-surface-900',
              arrow: 'hidden'
            }
          }
        }
      })
      app.directive('tooltip', Tooltip)
      app.mount('#app')

      toastInstance = app.config.globalProperties.$toast
      document.getElementById('app-loader')?.remove()
    } else {
      console.error('Authentication failed')
      await showBootstrapError(i18n)
    }
  } catch (err) {
    console.error('Bootstrap failed:', err)
    stopTokenRefresh()
    await showBootstrapError(i18n)
  }
}

function createCheckUserClient() {
  return ky.create({
    prefixUrl: `${import.meta.env.VITE_API_URL}`,
    hooks: {
      beforeRequest: [
        async (request) => {
          request.headers.set('Authorization', `Bearer ${keycloak.token}`)
        }
      ]
    }
  })
}

initKeycloak()
